import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { Review, ReSession } from "../interfaces/types";
import { addOrUpdateReviewInFirebase, fetchAllReviews } from "../services/firebaseService";
import { useUserContext } from "./userProvider";

export type ReviewContextType = {
  reviews: Review[];
  addReview: (newReview: Review) => void;
  updateReview: (updatedReview: Review) => void;
  getReviewsForSession: (session: ReSession) => Review[];
  getNumberOfReviewsForSession: (session: ReSession) => number;	
};

const ReviewContext = createContext<ReviewContextType>({
  reviews: [],
  addReview: () => { },
  updateReview: () => { },
  getReviewsForSession: () => [],
  getNumberOfReviewsForSession: () => 0,
});

export const ReviewProvider: FC<{ children: ReactNode; }> = ({ children }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const { user, userDetails } = useUserContext();

  useEffect(() => {
    refreshReviews();
  }, [user]);

  function refreshReviews(){
    if (user) {
      fetchAllReviews().then(fetchedReviews => {
        fetchedReviews = fetchedReviews?.filter(review => review.id);
        setReviews(fetchedReviews as Review[]);
      });
    }
  }

  function addReview(newReview: Review) {
    addOrUpdateReviewInFirebase(newReview);
    setReviews([...reviews, newReview]);
  }

  function updateReview(updatedReview: Review) {
    addOrUpdateReviewInFirebase(updatedReview);

    const updatedReviews = reviews?.map(review => {
      if (review.id === updatedReview.id) {
        return updatedReview;
      }
      return review;
    });
    setReviews(updatedReviews);
  }

  function getReviewsForSession (session: ReSession) {
    return reviews.filter(review => review.sessionId === session.id);
  }

  function getNumberOfReviewsForSession(session: ReSession) {
    return getReviewsForSession(session).length || 0;
  }

  const value: ReviewContextType = {
    reviews,
    addReview,
    updateReview,
    getReviewsForSession,
    getNumberOfReviewsForSession,
  };

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};

export const useReviewContext = (): ReviewContextType => {
  return useContext(ReviewContext);
};
